// The published price list, in one place.
//
// https://www.fleet-integrations.com/pricing.html ("Monthly price by fleet
// size"). One price per package for the whole fleet, banded by vehicle count:
// <25, 25+, 50+, then a custom quote at 100+. A 100+ fleet is quoted
// individually, but its estimate assumes AT LEAST the 50-99 price until staff
// key the negotiated figure (see packagePriceForVehicleCount). The old <10 band
// was retired 2026-09-23: every package now starts at $100, so the first band
// runs 1-24.
//
// Two pages price packages off this table -- the admin Account & Billing Stats
// page (tenant_manager.vue, every tenant at once) and the per-tenant Packages
// settings tab (tenant_config_packages.vue) -- and they have to agree, so the
// table and the band lookup live here rather than in either component.
// tenant_manager keeps thin method wrappers so its existing specs, which call
// the methods off the component's method bag, still exercise this code.
export const PACKAGE_PRICING = {
  basic: {
    label: 'Basic',
    tiers: [{ maxVehicles: 24, price: 100 }, { maxVehicles: 49, price: 150 }, { maxVehicles: 99, price: 200 }],
  },
  actuals: {
    label: 'Actuals',
    tiers: [{ maxVehicles: 24, price: 100 }, { maxVehicles: 49, price: 150 }, { maxVehicles: 99, price: 200 }],
  },
  views_reports: {
    label: 'Reports',
    tiers: [{ maxVehicles: 24, price: 100 }, { maxVehicles: 49, price: 150 }, { maxVehicles: 99, price: 200 }],
  },
  alerts: {
    label: 'Alerts',
    tiers: [{ maxVehicles: 24, price: 100 }, { maxVehicles: 49, price: 150 }, { maxVehicles: 99, price: 200 }],
  },
  vsm: {
    label: 'VSM',
    tiers: [{ maxVehicles: 24, price: 100 }, { maxVehicles: 49, price: 150 }, { maxVehicles: 99, price: 200 }],
  },
  erm: {
    label: 'ERM',
    tiers: [{ maxVehicles: 24, price: 100 }, { maxVehicles: 49, price: 150 }, { maxVehicles: 99, price: 200 }],
  },
  driver_manager: {
    label: 'Driver Mgr',
    tiers: [{ maxVehicles: 24, price: 100 }, { maxVehicles: 49, price: 150 }, { maxVehicles: 99, price: 200 }],
  },
  // data and maps are not sold on the public pricing page. kept at their
  // last published prices (data's old <10 price lifted to the $100 minimum
  // with the rest) so tenants still carrying the flags keep an estimate
  // instead of silently dropping a billing line.
  data: {
    label: 'Data',
    tiers: [{ maxVehicles: 49, price: 100 }, { maxVehicles: 99, price: 200 }],
  },
  maps: {
    label: 'Maps',
    defaultPrice: 0,
  },
}

export const PACKAGE_ORDER = ['basic', 'actuals', 'views_reports', 'alerts', 'vsm', 'erm', 'driver_manager', 'data', 'maps']

// the fleet size at which the website stops quoting a list price
export const CUSTOM_QUOTE_VEHICLE_COUNT = 100

// Basic Integration is the booking-platform <-> telematics link itself, so it is
// on only when BOTH sides are integrated (Ben, 2026-09-24). A booking platform
// with no telematics (Champion Coach, TBN-only) or telematics with no booking
// platform (a FLEET / CM-FLEET record) has no Basic line, whatever other
// packages it runs. Both pricing pages and the monthly unbilled-packages audit
// (fleet-int-api scripts/audit_common.py) derive it from here.
export const BOOKING_TENANT_TYPES = ['CM', 'TBN', 'FASTTRAK', 'MOOVS']

export function hasBookingPlatform(tenant) {
  return BOOKING_TENANT_TYPES.includes(String((tenant || {}).tenant_type || '').toUpperCase())
}

export function hasTelematicsPlatform(tenant) {
  const type = (tenant || {}).telematics_type
  return !!type && String(type).toUpperCase() !== 'NONE'
}

export function tenantHasBasicPackage(tenant) {
  return hasBookingPlatform(tenant) && hasTelematicsPlatform(tenant)
}

export function packagePriceForVehicleCount(packageKey, vehicleCount) {
  const packageDef = PACKAGE_PRICING[packageKey]
  if (!packageDef)
    return null
  if (packageDef.defaultPrice != null)
    return packageDef.defaultPrice

  const tiers = packageDef.tiers || []
  if (!tiers.length)
    return null
  // the website quotes 100+ fleets individually. The estimate assumes at
  // least the top published band (50-99) rather than reading Custom: a
  // negotiated figure keyed as an override replaces it, and until one is, a
  // 100+ fleet is never estimated below what a 99-vehicle fleet pays. (It used
  // to return null here, which priced every un-keyed package at nothing and
  // made the Account & Billing page show Stripe "over" the estimate.)
  if (vehicleCount >= CUSTOM_QUOTE_VEHICLE_COUNT)
    return tiers[tiers.length - 1].price

  for (const tier of tiers) {
    if (vehicleCount <= tier.maxVehicles)
      return tier.price
  }
  return tiers[tiers.length - 1].price
}

// Stripe product names -> our package keys. Stripe is where the names drift
// (a product gets renamed, a new subscription uses older wording), so match on
// substrings rather than an exact table.
export function normalizePackageLabel(label) {
  const norm = (label || '').toLowerCase()
  if (norm.includes('basic'))
    return 'basic'
  if (norm.includes('actuals'))
    return 'actuals'
  if (norm.includes('report') || norm.includes('views'))
    return 'views_reports'
  if (norm.includes('alert') || norm.includes('notification'))
    return 'alerts'
  if (norm.includes('vehicle status') || norm === 'vsm')
    return 'vsm'
  if (norm.includes('event response') || norm === 'erm')
    return 'erm'
  if (norm.includes('driver manager') || norm.includes('driver mgr') || norm.includes('scorecard'))
    return 'driver_manager'
  if (norm.includes('map feature'))
    return 'maps'
  // "Data Service Package (Routes)" is how the Data package is named on
  // some Stripe subscriptions, alongside the older "Data Access".
  if (norm.includes('data access') || norm.includes('data service'))
    return 'data'
  return norm.replace(/[^a-z0-9]/g, '')
}

// ── Alerts & Notifications: metered SMS ──────────────────────────────────
//
// The published Alerts price is a floor, not a cap: the page says "extra fees
// may apply based on SMS usage". A tenant whose metered SMS runs past its
// vehicle-tier price is billed off the usage instead.
//
// The usage figure is AVERAGE MONTHLY spend over the pricing window (90 days).
// Averaging over a long window stops one heavy or quiet month swinging the
// estimate the way a bare 30-day total did. The result is rounded UP to the
// next $25, the granularity Stripe subscriptions are actually keyed at --
// rounding up rather than to nearest so the quoted price never lands under
// the spend it is meant to recover.
//
// The average arrives pre-computed from the API (alerts_sms_avg_monthly_cost_90d)
// because only the server can see the usage rows. Early on it covers fewer than
// 90 days -- the usage table began collecting in July 2026 -- and the server
// divides by the days it actually has, so this multiplies a real monthly rate
// rather than a diluted one.
//
// Shared by the admin Account & Billing page and the per-tenant Packages tab,
// which have to quote the same tenant the same number.
export const SMS_ROUNDING_INCREMENT = 25

export function roundUpTo25(amount) {
  const value = Number(amount)
  if (!Number.isFinite(value) || value <= 0)
    return 0
  return Math.ceil(value / SMS_ROUNDING_INCREMENT) * SMS_ROUNDING_INCREMENT
}

// the metered figure Alerts is priced from. Exported so both pages can show
// the number behind the price.
//
// The figure arriving from the API is ALREADY marked up: alerts_sms_cost.py runs
// every total through sms_rates.with_processing_markup (AWS_SMS_PROCESSING_MULTIPLIER),
// whose own docstring calls itself "the ONLY place the multiplier is applied".
// This used to multiply again, which billed Alerts at 4x the carrier cost rather
// than 2x -- Windstar metered at $1475/mo against ~$372/mo of real carrier spend.
// Rounding up to the next $25 is all that belongs here.
export function alertsMeteredPrice(avgMonthlySmsCost) {
  return roundUpTo25(Number(avgMonthlySmsCost))
}

// A figure staff key against Alerts is not a fixed price: it stands in for
// the tier (a 100+ fleet has no list price, so the negotiated figure is the
// only tier it has) and is the FLOOR the metered SMS is measured against, so
// metered spend above it is still billed. Both pages resolve Alerts through
// this: max(keyed figure or list tier, metered SMS).
export function alertsBasePrice(tierPrice, overridePrice) {
  return overridePrice != null ? overridePrice : tierPrice
}

// smsActualsAvailable is false when we have no metered spend to price from --
// no SMS figures at all, or a tenant on a third-party sender whose messages
// never run through us. Either way the vehicle tier stands on its own.
export function alertsPriceFromSmsAndTier(tierPrice, avgMonthlySmsCost, smsActualsAvailable) {
  if (!smsActualsAvailable)
    return tierPrice == null ? null : tierPrice

  const metered = alertsMeteredPrice(avgMonthlySmsCost)
  // a 100+ fleet has no list price; its metered spend is the only number there
  if (tierPrice == null)
    return metered || null
  return Math.max(tierPrice, metered)
}

// ── Vehicle Status Manager: maintenance integrations ─────────────────────
//
// The published VSM price is a floor too: "Vehicle Status Manager may carry
// additional fees for integration with RTA or Fleetio". Syncing to either one
// adds a flat $50/mo on top of the vehicle-tier price.
//
// Flat, and charged ONCE: a tenant running both RTA and Fleetio pays one $50
// surcharge, not two.
export const VSM_INTEGRATION_SURCHARGE = 50

// the tenant flags that count as a paid maintenance integration. Deliberately
// excludes vehicle_status_management_tbn_status_enabled -- TBN status is not a
// third-party maintenance system and carries no fee.
// Every flag that means "this account syncs vehicles with a maintenance
// platform". The sub-flags are not redundant: Starline Collection and American
// Safety run fleetio_to_tbn with NEITHER base flag set, so a list of just the
// two base flags let them sync with Fleetio for free.
//
// Several flags can name the same platform, so labels are de-duplicated --
// otherwise a Fleetio account running DVIR sync too reads "Fleetio + Fleetio".
export const VSM_INTEGRATION_FLAGS = [
  { flag: 'vehicle_status_management_rta_enabled', label: 'RTA' },
  { flag: 'vehicle_status_management_fleetio_enabled', label: 'Fleetio' },
  { flag: 'vehicle_status_management_fleetio_dvir_enabled', label: 'Fleetio' },
  { flag: 'vehicle_status_management_fleetio_to_tbn_enabled', label: 'Fleetio' },
]

export function vsmPriceWithIntegrations(tierPrice, hasIntegration) {
  // a 100+ fleet has no list price to add to; the surcharge is part of whatever
  // that custom quote works out to, so don't invent a number here
  if (tierPrice == null)
    return null
  return tierPrice + (hasIntegration ? VSM_INTEGRATION_SURCHARGE : 0)
}

// `isOn` is the caller's own flag test, because the two callers read tenant
// flags differently (a settings model vs a projected tenant record).
export function vsmIntegrationLabels(isOn) {
  const platforms = []
  for (const entry of VSM_INTEGRATION_FLAGS) {
    if (isOn(entry.flag) && platforms.indexOf(entry.label) === -1)
      platforms.push(entry.label)
  }
  return platforms
}

// ── Manual monthly price overrides ───────────────────────────────────────
//
// Staff key a negotiated figure per package into
// `tenant_manager_pricing_monthly_override` on the tenant record, shaped
// { packages: { <packageKey>: "<amount as string>" } }. It is what a 100+ fleet
// (no list price, quoted individually) gets priced at, and it wins over the
// list price anywhere it is set.
//
// Both the admin Account & Billing page and the per-tenant Packages tab read
// and write this field, so the shape and the parsing live here.
export const PRICING_OVERRIDE_FIELD = 'tenant_manager_pricing_monthly_override'

// -> { <packageKey>: "<trimmed string>" }, dropping blanks
export function normalizePricingOverridePackages(raw) {
  const packages = {}
  if (!raw || typeof raw !== 'object')
    return packages
  const source = raw.packages
  if (!source || typeof source !== 'object')
    return packages
  for (const [packageKey, value] of Object.entries(source)) {
    if (value != null && String(value).trim() !== '')
      packages[packageKey] = String(value).trim()
  }
  return packages
}

// tolerant of "$250", "250.00", " 250 " -- staff type into a free-text box
export function parseOverrideAmount(value) {
  if (value == null || value === '')
    return null
  const cleaned = String(value).replace(/[^0-9.-]/g, '')
  if (!cleaned)
    return null
  const amount = Number(cleaned)
  return Number.isFinite(amount) ? amount : null
}

// the record field value for a set of overrides: null once the last one is
// cleared, so the field is removed rather than left as an empty husk
export function buildPricingOverrideValue(packages) {
  const cleaned = normalizePricingOverridePackages({ packages })
  return Object.keys(cleaned).length ? { packages: cleaned } : null
}
